/**
 * 좌측 IconRail — ai- 시리즈 4 앱 진입점.
 * ProjectLayout 안 split-layout 의 첫 자식 (사이드바 왼쪽 좁은 영역).
 * 현재 앱(ai-readme) 은 active 표시, 다른 앱 클릭 시 환경(dev/prod)에 맞는 도메인으로 navigate.
 * 아이콘은 각 앱의 파비콘 이미지를 사용 (시각적 통일성).
 */
const CURRENT_APP = 'ai-readme'

interface AppEntry {
  key: string
  label: string
  url: string
  favicon: string
}

const ENV = (import.meta as unknown as { env: Record<string, string | undefined> }).env

/** 현재 frontend 가 어디서 도는지 — readme.ainecto.com 만 prod 로 간주, 그 외(localhost/dev)는 dev. */
const HOST = typeof window !== 'undefined' ? window.location.hostname : ''
const IS_PROD = HOST === 'readme.ainecto.com'

const APP_URL = {
  'ai-readme':  IS_PROD ? 'https://readme.ainecto.com'    : 'https://readme-dev.ainecto.com',
  'ai-erd':     IS_PROD ? 'https://ai-erd.com'            : 'https://dev.ai-erd.com',
  'ai-diagram': IS_PROD ? 'https://ai-diagram.com'        : 'https://dev.ai-diagram.com',
  'ai-test':    IS_PROD ? 'https://testsuite.ainecto.com' : 'https://testsuite-dev.ainecto.com',
} as const

const APPS: AppEntry[] = [
  {
    key: 'ai-readme',
    label: 'AI-README — 문서',
    url: ENV.VITE_APP_AI_README ?? APP_URL['ai-readme'],
    favicon: '/images/favicon-32.png',
  },
  {
    key: 'ai-erd',
    label: 'AI-ERD — DB 다이어그램',
    url: ENV.VITE_APP_AI_ERD ?? APP_URL['ai-erd'],
    favicon: 'https://ai-erd.com/img/AI_ERD_Icon_Logo.png',
  },
  {
    key: 'ai-diagram',
    label: 'AI-Diagram — 시스템 다이어그램',
    url: ENV.VITE_APP_AI_DIAGRAM ?? APP_URL['ai-diagram'],
    favicon: 'https://dev.ai-diagram.com/img/AI_Diagram_Icon.png',
  },
  {
    key: 'ai-test',
    label: 'AI-Test — 테스트케이스',
    url: ENV.VITE_APP_AI_TEST ?? APP_URL['ai-test'],
    favicon: 'https://testsuite-dev.ainecto.com/favicon.ico',
  },
]

interface IconRailProps {
  /** 사이드바 collapsed 여부 — toggle 아이콘 방향에 사용 */
  sidebarCollapsed?: boolean
  /** 사이드바 toggle 핸들러 — IconRail 하단 버튼이 호출 */
  onToggleSidebar?: () => void
}

export function IconRail({ sidebarCollapsed, onToggleSidebar }: IconRailProps = {}) {
  return (
    <nav className="icon-rail" aria-label="Apps">
      {/* 상단 — 사이드바 toggle (앱 아이콘과 시각적으로 구분) */}
      {onToggleSidebar && (
        <button type="button" id="btn-sidebar-toggle-rail" className="icon-rail-item icon-rail-toggle"
                title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                onClick={onToggleSidebar}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {sidebarCollapsed ? <path d="m9 18 6-6-6-6"/> : <path d="m15 18-6-6 6-6"/>}
          </svg>
        </button>
      )}
      {APPS.map((app) => {
        const active = app.key === CURRENT_APP
        return (
          <a key={app.key} href={app.url}
             className={`icon-rail-item icon-rail-favicon${active ? ' active' : ''}`}
             title={app.label}
             aria-label={app.label}
             aria-current={active ? 'page' : undefined}>
            <img src={app.favicon} alt="" width={24} height={24} loading="lazy" />
          </a>
        )
      })}
    </nav>
  )
}
